import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithClient, useUserClientContext } from '@/app/clientContext';
import { Descope, useDescope, useSession, useUser } from '@descope/react-sdk';
import { getAppConfig } from '@/app/config';
import { Flex, Loader, Paper } from '@mantine/core';
import styles from './RequireAuth.module.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useClientCodeFromUrl } from '@/hooks/useClientCodeFromUrl';
import { parseDeliveryIdentifier } from '@/hooks/useDeliveryIdentifier';
import { authContext } from '@/app/auth';
import { captureException } from '@sentry/react';
import {
  ClientContextNotFoundError,
  HttpUnauthorizedError,
} from '@/features/types';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';

const errorNotificationData = (error: unknown) => ({
  title:
    error instanceof HttpUnauthorizedError ? 'Authentication Error' : 'Error',
  message:
    error instanceof HttpUnauthorizedError
      ? error.message
      : 'Something went wrong',
  color: 'red' as const,
  icon: <IconAlertCircle size="1rem" />,
  autoClose: 4000,
});

export const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { hydrateUserAndClientContext, signout, isUserLoaded } =
    useUserClientContext();

  const navigate = useNavigate();
  const descopeFlowId = getAppConfig().descopeLoginFlowId;
  const { isAuthenticated } = useSession();
  const { user, isUserLoading } = useUser();

  const clientCode = useClientCodeFromUrl();
  const [searchParams] = useSearchParams();
  // The client code can arrive as the /client/:code path segment or as a
  // ?client= query param; either way it is only a hint, applied by the
  // provider solely for superusers. The path segment is the explicit
  // destination, so it wins over a lingering ?client= left behind by the
  // previously viewed client (the param persists across navigation). Sourcing
  // it here lets the initial hydration target the right client, so a
  // /client/:code load needs only one hydration instead of a default-client
  // pass followed by a second for the real client. A ?delivery= resolves its
  // own client on the backend, so it owns the choice and any hint alongside it
  // would only impersonate a client the delivery may not belong to.
  const urlClientCodeHint = parseDeliveryIdentifier(searchParams.toString())
    ? undefined
    : (clientCode ?? searchParams.get('client')?.toUpperCase());

  const [errored, setErrored] = useState(false);
  const [isDescopeLoading, setIsDescopeLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { logout } = useDescope();

  const hydrateWithUser = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (u: any): Promise<void> => {
      return signInWithClient(
        hydrateUserAndClientContext,
        u.email,
        undefined,
        urlClientCodeHint
      );
    },
    [hydrateUserAndClientContext, urlClientCodeHint]
  );

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      // Important: call logout() without passing the click event
      await logout();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Logout failed', err);
    } finally {
      // call "old" signout to clear client context.
      signout();
      setIsLoggingOut(false);
      setErrored(false);
      navigate('/', { replace: true });
    }
  }, [isLoggingOut, logout, navigate, signout]);

  const hydratingRef = useRef(false);
  const attemptHydrate = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (u: any): Promise<void> => {
      // ignore if this callback is already running
      if (hydratingRef.current) return;
      hydratingRef.current = true;

      try {
        await hydrateWithUser(u);
      } catch (error) {
        // The client-context request response indicates the client, delivery, or release in
        // the URL doesn't exist; the session itself is fine, so show the 404 page instead of
        // logging out.
        if (error instanceof ClientContextNotFoundError) {
          navigate('/404', { replace: true });
          return;
        }

        setErrored(true);
        captureException(error);
        notifications.show(errorNotificationData(error));

        await handleLogout();
      } finally {
        hydratingRef.current = false;
      }
    },
    [handleLogout, hydrateWithUser, navigate]
  );

  /**
   * This effect manually hydrates the user and client context into app state
   * in the case that the page is loaded with an existing Descope session.
   */
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (isUserLoaded || isUserLoading || !user || errored) return;

      try {
        // prevent state updates after unmount
        if (!cancelled) {
          await attemptHydrate(user);
        }
      } catch {
        // attemptHydrate already logs/handles; this is just a safety net
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [isUserLoading, user, attemptHydrate, errored, isUserLoaded]);

  const auth = {
    isLoggingOut,
    logOut: handleLogout,
  };

  if (isAuthenticated && isUserLoaded && !errored) {
    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get('redirect_to');
    if (
      redirectTo &&
      redirectTo.startsWith('/') &&
      !redirectTo.startsWith('//')
    ) {
      window.location.href = redirectTo;
      return null;
    }
    return <authContext.Provider value={auth}>{children}</authContext.Provider>;
  }

  const showLoaderForDescope = isDescopeLoading && !isAuthenticated;
  const showLoaderForClientContext = isAuthenticated && !isUserLoaded;

  return (
    <div className={styles.authBackground}>
      <div className={styles.authContainer}>
        {(showLoaderForDescope || showLoaderForClientContext) && (
          <Paper shadow="md" p="xl" radius="md" withBorder w={560} h={450}>
            <Flex justify="center" align="center" h="100%">
              <Loader />
            </Flex>
          </Paper>
        )}
        {!isAuthenticated && (
          <div style={{ display: !showLoaderForDescope ? 'block' : 'none' }}>
            {/* the above hides the Descope form while loading to avoid a flicker when both the Descope component and loader might show momentarily */}
            <Descope
              flowId={descopeFlowId}
              onReady={() => setIsDescopeLoading(false)}
              onSuccess={(e) => void attemptHydrate(e.detail.user)}
              styleId="overstory"
            />
          </div>
        )}
      </div>
    </div>
  );
};
